import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "primereact/dropdown";
import { Paginator } from "primereact/paginator";
import { useHistory } from "react-router";
import { BreadCrumb } from "primereact/breadcrumb";
import { Toast } from "primereact/toast";
import * as constants from "../app_utils/constants/Constants";
import {
  DATA_BUNDLES_ROUTE_PATH,
  HOME_ROUTE_PATH,
} from "../app_utils/route_paths/resolver/PageRoutes";
import { PrimeIcons } from "primereact/api";
import { BaseApiServiceImpl } from "../app_utils/api/BaseApiServiceImpl";
import { MessageUtils } from "../app_utils/utils/MessageUtils";
import { paginatorTemplate } from "../app_utils/components/PaginatorTemplate";

const STATUS_OPTIONS = [
  { label: "All Statuses", value: null },
  { label: "Pending", value: "pending" },
  { label: "Processing", value: "PROCESSING" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Failed", value: "FAILED" },
];

const statusBodyTemplate = (row: any) => {
  const status = (row?.status || "").toUpperCase();
  let className = "p-tag p-tag-warning";
  if (status === "COMPLETED") className = "p-tag p-tag-success";
  else if (status === "FAILED") className = "p-tag p-tag-danger";
  return <span className={className}>{row?.status || "-"}</span>;
};

const DataBundleReportsView = () => {
  const [records, setRecords] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTermFilter, setSearchTermFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [first, setFirst] = useState<number>(0);
  const [limit, setLimit] = useState<number>(constants.MAXIMUM_RECORDS_PER_PAGE);
  const offsetRef = useRef<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  const toast = useRef<Toast>(null);
  const history = useHistory();

  const breadcrumbHome = {
    icon: "pi pi-home",
    command: () => {
      history.push(HOME_ROUTE_PATH);
    },
  };

  const breadcrumbItems = [
    {
      label: "Data Bundle Reports",
      icon: PrimeIcons.TABLET,
    },
  ];

  const getQueryParameters = () => {
    let searchParameters: any = { offset: offsetRef.current, limit: limit };
    if (searchTermFilter !== null) searchParameters.searchTerm = searchTermFilter;
    if (statusFilter !== null) searchParameters.status = statusFilter;
    return searchParameters;
  };

  const fetchRecordsFromServer = () => {
    setIsLoading(true);
    new BaseApiServiceImpl("/api/v1/data-bundles/ui")
      .getRequestWithJsonResponse(getQueryParameters())
      .then(async (response) => {
        setIsLoading(false);
        setRecords(response?.records ?? []);
        setTotalItems(response?.totalItems ?? 0);
      })
      .catch((error) => {
        setIsLoading(false);
        MessageUtils.showErrorMessage(toast, error.message);
      });
  };

  useEffect(() => {
    fetchRecordsFromServer();
  }, []);

  const onSubmitFilter = () => {
    offsetRef.current = 0;
    setFirst(0);
    fetchRecordsFromServer();
  };

  const refreshStatus = async (row: any) => {
    const reference = row?.kacyberReference || row?.externalReference;
    if (!reference) return;
    setIsRefreshing(true);
    try {
      await new BaseApiServiceImpl(`/api/v1/data-bundles/status/${reference}`).getRequestWithJsonResponse({});
      toast.current?.show({
        severity: "success",
        summary: "Status Refreshed",
        detail: `Status for ${reference} updated.`,
      });
      fetchRecordsFromServer();
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
      });
    }
    setIsRefreshing(false);
  };

  const actionBodyTemplate = (row: any) => (
    <Button
      icon="pi pi-refresh"
      className="p-button-text p-button-sm"
      tooltip="Refresh Status"
      loading={isRefreshing}
      onClick={() => refreshStatus(row)}
    />
  );

  const onPage = (event: any) => {
    setLimit(event.rows);
    setFirst(event.first);
    offsetRef.current = event.first;
    fetchRecordsFromServer();
  };

  return (
    <div className="card">
      <div className="p-d-flex p-jc-between">
        <BreadCrumb home={breadcrumbHome} model={breadcrumbItems} />
      </div>
      <div className="grid mt-3">
        <div className="col-12 md:col-4">
          <label htmlFor="search" className="w-full p-d-block">
            Search
          </label>
          <input
            id="search"
            type="text"
            className="form-control w-full"
            placeholder="Search by reference"
            value={searchTermFilter || ""}
            onChange={(e) => setSearchTermFilter(e.target.value)}
          />
        </div>
        <div className="col-12 md:col-4">
          <label htmlFor="status" className="w-full p-d-block">
            Status
          </label>
          <Dropdown
            id="status"
            value={statusFilter}
            options={STATUS_OPTIONS}
            onChange={(e) => setStatusFilter(e.value)}
            placeholder="Select status"
            className="w-full"
          />
        </div>
        <div className="col-12 md:col-2 mt-4">
          <Button
            label="Filter"
            icon="pi pi-filter"
            className="p-button-primary mr-1"
            onClick={onSubmitFilter}
          />
          <Button
            label="Buy Bundles"
            icon="pi pi-shopping-cart"
            className="p-button-success ml-1"
            onClick={() => history.push(DATA_BUNDLES_ROUTE_PATH)}
          />
        </div>
      </div>

      <DataTable
        value={records || []}
        loading={isLoading}
        responsiveLayout="scroll"
        emptyMessage="No data bundle purchases found."
        className="mt-3"
      >
        <Column field="dateCreated" header="Date" />
        <Column field="externalReference" header="Your Reference" />
        <Column field="kacyberReference" header="KaCyber Reference" />
        <Column field="totalRecipients" header="Recipients" />
        <Column field="totalSuccessful" header="Successful" />
        <Column field="totalFailed" header="Failed" />
        <Column field="currency" header="Currency" />
        <Column field="totalAmount" header="Total Amount" />
        <Column field="status" header="Status" body={statusBodyTemplate} />
        <Column body={actionBodyTemplate} style={{ width: "4rem" }} />
      </DataTable>

      <Paginator
        first={first}
        rows={limit}
        totalRecords={totalItems}
        alwaysShow={true}
        onPageChange={onPage}
        template={paginatorTemplate}
      />

      <Toast ref={toast} />
    </div>
  );
};

export default DataBundleReportsView;
